import { useState } from 'react';
import { useMutation } from '@apollo/client';
import cn from 'classnames';
// Api
import {
  REMOVE_PAYMENT_METHOD,
  SET_PAYMENT_METHOD_AS_DEFAULT,
} from 'api/payments/mutations';
import { MY_PAYMENT_METHODS } from 'api/payments/queries';
// Types
import {
  SetPaymentMethodAsDefault,
  SetPaymentMethodAsDefaultVariables,
} from 'api/payments/types/SetPaymentMethodAsDefault';
import {
  RemovePaymentMethod,
  RemovePaymentMethodVariables,
} from 'api/payments/types/RemovePaymentMethod';
import { PaymentCardProps } from 'helpers/payments.types';
// Helpers
import { getIsExpired, formatExpiryLabel } from 'helpers/payments';
// Ui
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Text from 'ui3/Text/Text';
import NotificationModal from 'ui3/NotificationModal/NotificationModal';
// Components
import { showToast } from 'components/common/Toast/Toast';
// Styles
import styles from './Card.module.scss';

export const Card = ({ payment, index, onSelect }: PaymentCardProps) => {
  const { id, methodId, last4, cardholderName, expMonth, expYear } = payment;

  const [setPaymentMethodAsDefault] = useMutation<
    SetPaymentMethodAsDefault,
    SetPaymentMethodAsDefaultVariables
  >(SET_PAYMENT_METHOD_AS_DEFAULT, {
    refetchQueries: [MY_PAYMENT_METHODS],
  });

  const [
    removePaymentMethod,
    { loading: removePaymentMethodLoading },
  ] = useMutation<RemovePaymentMethod, RemovePaymentMethodVariables>(
    REMOVE_PAYMENT_METHOD,
    { refetchQueries: [MY_PAYMENT_METHODS] }
  );

  const [
    deletePaymentModalVisible,
    setDeletePaymentModalIsVisible,
  ] = useState<boolean>(false);

  const isActive = payment.isDefault;
  const isExpired = getIsExpired(expMonth, expYear);
  const cardLabel = `Card No${index + 1}`;
  const expiryLabel = formatExpiryLabel(expMonth, expYear);

  const onPaymentSelect = async () => {
    if (onSelect) {
      onSelect(methodId, cardholderName || '');
    }

    if (!payment.isDefault) {
      try {
        await setPaymentMethodAsDefault({
          variables: { id },
        });

        showToast({
          type: 'success',
          message: 'Default payment method has been changed successfully',
        });
      } catch (error) {
        showToast({
          message: error?.message || '',
          type: 'error',
        });
      }
    }
  };

  const onDeletePaymentModalOpen = () => {
    setDeletePaymentModalIsVisible(true);
  };

  const onDeletePaymentModalClose = () => {
    setDeletePaymentModalIsVisible(false);
  };

  const handleDeleteClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    onDeletePaymentModalOpen();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void onPaymentSelect();
    }
  };

  const handleDeletePayment = async () => {
    try {
      await removePaymentMethod({
        variables: { id: payment?.id },
      });
      showToast({
        message: 'Payment card has been deleted successfully.',
      });
    } catch (err) {
      showToast({
        message: 'Looks like something went wrong. Please try again later.',
        type: 'error',
      });
    }
    onDeletePaymentModalClose();
  };

  return (
    <>
      <div
        className={cn(styles.root, {
          [styles.active]: isActive,
        })}
        onClick={onPaymentSelect}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <Text variant="subtitle2" className={styles.cardTitle}>
              {cardLabel}
            </Text>
            {isActive && <span className={styles.defaultBadge}>Default</span>}
            {isExpired && <span className={styles.expiredBadge}>Expired</span>}
          </div>

          <button
            className={styles.cardAction}
            onClick={handleDeleteClick}
            type="button"
            aria-label="Delete card"
          >
            <MillionsIcon name="trash" className={styles.cardActionIcon} />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.bodyTextGroup}>
            <Text variant="body3Regular" color="lights-high">
              {cardholderName || 'Cardholder'}
            </Text>
            <Text
              variant="body3Regular"
              color="lights-low"
              className={styles.cardNumber}
            >
              **** **** **** {last4}
            </Text>
          </div>

          <Text variant="captionRegular" color="lights-low">
            {expiryLabel}
          </Text>
        </div>
      </div>

      <NotificationModal
        open={deletePaymentModalVisible}
        type="confirm"
        title="Delete card"
        description={
          <>
            Are You sure to delete card <b>**** ${last4}</b>
          </>
        }
        onClose={onDeletePaymentModalClose}
        acceptLabel="Delete"
        onAcceptClick={handleDeletePayment}
        loading={removePaymentMethodLoading}
        acceptDisabled={removePaymentMethodLoading}
        withCancelButton
      />
    </>
  );
};
